import React, { useState, useRef } from 'react';
import './EgRegressionChart.css';

/**
 * Einfache grafische Regression: Ausprägungen (x) vs. Sollzeit (y),
 * mit Regressionslinie, Trompetenkurve (95%-Prediction-Band) und Einzelwerten.
 * Bei Hovern: Tooltip mit Messpunkt-Nr und Inhalt (pointMeta); bei mehreren in Reichweite als Liste.
 * @param {{ points: {x: number, y: number}[], pointMeta?: { number: number, activity: string }[], categories: string[], regression, formatTime, darkMode?: boolean, width?: number, height?: number }} props
 */
function EgRegressionChart({ points, pointMeta, categories, regression, formatTime, darkMode = false, width = 480, height = 280 }) {
  const [tooltip, setTooltip] = useState(null);
  const circleCoordsRef = useRef([]);

  const margin = { left: 52, right: 12, top: 14, bottom: 40 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  const k = categories.length;

  if (k === 0 || points.length === 0) {
    return (
      <div className={`eg-regression-chart ${darkMode ? 'dark-mode' : ''}`} style={{ width, height }}>
        <div className="eg-regression-chart-empty">Keine Daten für eine Darstellung.</div>
      </div>
    );
  }

  const yVals = points.map(p => p.y);
  let maxY = Math.max(...yVals);
  let minY = Math.min(0, ...yVals);

  // Band und Linie: Stützstellen von 0.5 bis k+0.5
  let bandPath = '';
  let linePath = '';
  const t = 2.0; // ~95% Prediction

  if (regression && regression.s > 0 && regression.Sxx > 0) {
    const { slope, intercept, s, Sxx, xMean, n } = regression;
    const pts = [];
    for (let i = 0; i <= 30; i++) {
      const x = 0.5 + (i / 30) * (k);
      const yHat = slope * x + intercept;
      const hw = t * s * Math.sqrt(1 + 1 / n + (x - xMean) ** 2 / Sxx);
      pts.push({ x, yU: yHat + hw, yL: Math.max(0, yHat - hw), yHat });
    }
    maxY = Math.max(maxY, ...pts.map(p => p.yU));
    minY = Math.min(minY, ...pts.map(p => p.yL));

    bandPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yU}`).join(' ') + ' ' +
      pts.slice().reverse().map(p => `L ${p.x} ${p.yL}`).join(' ') + ' Z';
    linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yHat}`).join(' ');
  } else if (regression) {
    const yHat = regression.intercept;
    linePath = `M 0.5 ${yHat} L ${k + 0.5} ${yHat}`;
  }

  const rangeY = maxY - minY || 1;
  const padY = rangeY * 0.08;
  const yMax = maxY + padY;
  const yMin = Math.max(0, minY - padY);
  const yRange = yMax - yMin || 1;

  const xScale = (x) => margin.left + ((x - 0.5) / Math.max(1, k)) * plotW;
  const yScale = (y) => margin.top + plotH - ((y - yMin) / yRange) * plotH;

  // Punkte mit leichtem Jitter pro x, damit überlagerte Einzelwerte sichtbar bleiben
  const jitterByX = {};
  const pointsWithJitter = points.map((p, i) => {
    const kx = p.x;
    const j = (jitterByX[kx] = (jitterByX[kx] || 0) + 1);
    const jitter = (j % 5 - 2) * 0.07;
    return { x: p.x + jitter, y: p.y };
  });

  const toSvg = (pathStr) => {
    if (!pathStr) return '';
    return pathStr.replace(/([ML])\s*([\d.]+)\s+([\d.]+)/g, (_, cmd, x, y) =>
      `${cmd} ${xScale(parseFloat(x))} ${yScale(parseFloat(y))}`
    );
  };

  const yTicks = 5;
  const yTickVals = [];
  for (let i = 0; i <= yTicks; i++) {
    yTickVals.push(yMin + (yMax - yMin) * (i / yTicks));
  }

  circleCoordsRef.current = pointsWithJitter.map(p => ({ cx: xScale(p.x), cy: yScale(p.y) }));

  const handleMouseMove = (e) => {
    if (!pointMeta || pointMeta.length !== points.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const HIT_R = 25;
    const hits = [];
    for (let i = 0; i < circleCoordsRef.current.length; i++) {
      const { cx, cy } = circleCoordsRef.current[i];
      if (Math.hypot(mx - cx, my - cy) < HIT_R) hits.push(i);
    }
    if (hits.length > 0) {
      const entries = hits.map(i => pointMeta[i]).sort((a, b) => (a.number || 0) - (b.number || 0));
      setTooltip({ entries, clientX: e.clientX, clientY: e.clientY });
    } else {
      setTooltip(null);
    }
  };

  const handleMouseLeave = () => setTooltip(null);

  return (
    <div
      className={`eg-regression-chart ${darkMode ? 'dark-mode' : ''}`}
      style={{ width, height }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <svg width={width} height={height} className="eg-regression-svg">
        <g className="eg-regression-plot">
          {/* Trompetenkurve (Prediction-Band) */}
          {regression && bandPath && (
            <path
              d={toSvg(bandPath)}
              className="eg-regression-band"
              stroke="none"
            />
          )}
          {/* Regressionslinie */}
          {regression && linePath && (
            <path
              d={toSvg(linePath)}
              className="eg-regression-line"
              fill="none"
              stroke="var(--eg-regression-line, #667eea)"
              strokeWidth="2"
            />
          )}
          {/* Einzelwerte */}
          {pointsWithJitter.map((p, i) => (
            <circle
              key={i}
              cx={xScale(p.x)}
              cy={yScale(p.y)}
              r="4"
              className="eg-regression-point"
              fill="var(--eg-regression-point, #6366f1)"
              stroke="var(--eg-regression-point-stroke, #fff)"
              strokeWidth="1.5"
            />
          ))}
        </g>
        {/* Y-Achse */}
        {yTickVals.map((v, i) => (
          <g key={i}>
            <line x1={margin.left} y1={yScale(v)} x2={margin.left - 4} y2={yScale(v)} className="eg-regression-tick" />
            <text x={margin.left - 6} y={yScale(v)} textAnchor="end" dominantBaseline="middle" className="eg-regression-label">{formatTime(v)}</text>
          </g>
        ))}
        <line x1={margin.left} y1={margin.top} x2={margin.left} y2={margin.top + plotH} className="eg-regression-axis" />
        {/* X-Achse: Kategorien */}
        {categories.map((lab, i) => (
          <text
            key={i}
            x={xScale(i + 1)}
            y={margin.top + plotH + 20}
            textAnchor="middle"
            className="eg-regression-label eg-regression-xlabel"
            style={{ fontSize: categories.some(c => c.length > 8) ? 10 : 11 }}
          >
            {lab.length > 12 ? lab.slice(0, 10) + '…' : lab}
          </text>
        ))}
        <line x1={margin.left} y1={margin.top + plotH} x2={margin.left + plotW} y2={margin.top + plotH} className="eg-regression-axis" />
      </svg>
      {regression && regression.r2 != null && (
        <div className="eg-regression-legend">
          R² = {regression.r2.toFixed(3)} {regression.r2 >= 0.01 ? '→ zeitbestimmend' : '→ kaum zeitbestimmend'}
        </div>
      )}
      {tooltip && (
        <div
          className="eg-regression-tooltip"
          style={{ left: tooltip.clientX + 10, top: tooltip.clientY + 10 }}
        >
          {tooltip.entries.map((e, i) => (
            <div key={i}>Nr {e.number} – {typeof e.activity === 'string' && e.activity.length > 60 ? e.activity.slice(0, 57) + '…' : (e.activity || '-')}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EgRegressionChart;
